import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FilterDescriptor, LoadOptions, SortDescriptor } from 'devextreme/data';
import CustomStore, { CustomStoreOptions } from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LoadResult } from 'devextreme/common/data/custom-store';
import { DataSourceLoadOptions, MediumService } from 'src/api/generated';
import { BaseApiService } from './base-api.service';

export interface ZeroxCustomStoreOptions<
  TItem = any,
  TKey = any,
  > extends CustomStoreOptions<TItem, TKey> {

  /**
   * Specifies a custom implementation of the load(options) method.
   */
  loadAsObservable: ((options: LoadOptions<TItem>) =>
    | Observable<LoadResult<TItem>>);
}

export class ZeroxCustomStore<
  TItem = any,
  TKey = any,
  > extends CustomStore<TItem, TKey> {
  constructor(options?: ZeroxCustomStoreOptions<TItem, TKey>) {
    super(options as CustomStoreOptions<TItem, TKey>);

    this.loadAsObservable = options?.loadAsObservable;
  }

  loadAsObservable?: ((options: LoadOptions<TItem>) =>
    | Observable<LoadResult<TItem>>) = undefined;
}



@Injectable({
  providedIn: 'root'
})
export class CommonsService {

  constructor(private http: HttpClient) { }

  /**
   * Generates new datasource for soem DevExteme component
   * @param lastRecord last record behavior subject
   * @param adjustFilter filter extender (reducer) callback
   * @param adjustSort sort extender callback
   * @returns custom store
   */
  public makeAsyncDataSource<
    TItem = any,
    TKey = any,
    TService extends BaseApiService<TItem> = any
    >(
      service: TService,
      lastRecord?: BehaviorSubject<TItem | undefined>,
      adjustFilter?: ((filter: FilterDescriptor | Array<FilterDescriptor>) => FilterDescriptor | Array<FilterDescriptor>),
      adjustSort?: ((sort?: SortDescriptor<TItem> | Array<SortDescriptor<TItem>>) => SortDescriptor<TItem> | Array<SortDescriptor<TItem>>)
    ): DataSource<TItem, TKey> {
    const self = this;
    const adjustFilterCallback = adjustFilter;

    function isNotEmpty(value: TKey): boolean {
      const tmp = value !== undefined && value !== null;
      if (typeof value === 'number') return tmp && (<number>value) > -1;
      if (typeof value === 'string') return tmp && (<string>value) !== '';
      if (Array.isArray(value)) return tmp && (<Array<string>>value).length > 0;
      return false;
    }


    function toPromise<T>(o: Observable<T>): PromiseLike<T> {
      return new Promise((resolve, reject) => {
        o.subscribe({
          next: res => {
            resolve(res);
          },
          error: (err: HttpErrorResponse) => {
            reject(err)
          }
        })
      }) as Promise<T>;
    }

    // copy 2 objects field by field, trying to match fields
    function copyFields<TF, T>(from: TF): T {
      var to = <T>{};
      for (const key in from) {
        if ((from as any).hasOwnProperty(key)) {
          (to as any)[key] = (from as any)[key];
        }
      }
      return to;
    }

    return new DataSource<TItem, TKey>({
      store:
        new ZeroxCustomStore<TItem, TKey>({
          loadMode: "processed",
          cacheRawData: false,

          key: 'Id',

          remove: function (key: TKey): PromiseLike<any> {
            try {
              return toPromise(service.softDelete(key + "", 'body'));
            } catch (e) {
              console.error('Service does not have _delete method');
              return Promise.resolve(0);
            }
          },

          byKey: function (key: TKey): PromiseLike<TItem> {
            let tmpKey = key + '';
            if (!isNotEmpty(key)) {
              return Promise.resolve(<TItem>{});
            }

            if (lastRecord != null) {
              try {
                return toPromise(service.getOne(tmpKey, 'body').pipe(map((rec: TItem) => {
                  lastRecord.next(rec);
                  return rec;
                })));
              } catch (e) {
                console.error('Service does not have getOne method');
                return Promise.resolve(<TItem>{});
              }
            } else {
              try {
                return toPromise(service.getOne(tmpKey, 'body'));
              } catch (e) {
                console.error('Service does not have getOne method');
                return Promise.resolve(<TItem>{});
              }
            }
          },

          load: function (options: LoadOptions): PromiseLike<LoadResult<TItem>> {
            return toPromise(this.loadAsObservable(options));
          },

          loadAsObservable: function (options: LoadOptions): Observable<LoadResult<TItem>> {
            let doDistinctType = false;
            let doDistinctColumn = "";

            // attempt to manage distinct valuse call on mongodb it must be do by this speculative manner due to lack of Group management
            // if (options.group != undefined) {
            //   const grp = options.group;
            //   if (Array.isArray(grp) && grp.length >= 1) {
            //     const g1 = <any>grp[0];
            //     if (isNotEmpty(g1["selector"])) {
            //       doDistinctType = true;
            //       doDistinctColumn = g1["selector"];
            //       options.group = undefined;
            //     }
            //   }
            // }
            // extend filter for combos
            if (options["searchExpr"] != undefined && options["searchOperation"] != undefined && isNotEmpty(options["searchValue"])) {
              if (options["filter"] != null) {
                options["filter"] = [
                  options["filter"],
                  "and",
                  [options["searchExpr"], options["searchOperation"], options["searchValue"]]
                ];
              } else {
                options["filter"] = [options["searchExpr"], options["searchOperation"], options["searchValue"]];
              }
            }

            // extend filter by external function
            if (adjustFilterCallback != null) {
              options["filter"] = adjustFilterCallback(options["filter"] as Array<string> | null);
            }

            //*************
            //OVERRIDE SORT
            //*************
            if (adjustSort != null) {
              options.sort = adjustSort(options.sort);
            }

            if (doDistinctType) {
              try { 
                // problems with distinct values !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!  we need copy of options to avoid changes in original object, but we need copy field by field, cause types are not equal
                return service.postDistinctValues(doDistinctColumn, copyFields<LoadOptions, DataSourceLoadOptions>(options)).pipe(
                  /*
                    map translates [val0, val1, ... ,valN] -> {data:[{key:val0}, {key:val1}, ... , {key:valN}]}
                  */
                  map(result => ({ data: (result as Array<TItem>).map(el => ({ key: el })) }))
                );
              } catch (e) {
                console.error('Service does not have postDistinctValues method');
                return new Observable<LoadResult<TItem>>();
              }
            }
            else {
              try {
                return service.postList(copyFields<LoadOptions, DataSourceLoadOptions>(options));
              } catch (e) {
                console.error('Service does not have postList method');
                return new Observable<LoadResult<TItem>>();
              }
            }
          }
        })
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class TrashCommonsService {

  constructor() { }

  /**
   * Generates new datasource for soem DevExteme component
   * @param service service to be used
   * @param key key of medium
   * @param adjustFilter filter extender (reducer) callback
   * @param adjustSort sort extender callback
   * @returns custom store
   */
  public makeAsyncDataSource<
    TItem extends any = any,
    TKey = any
    >(
      service: any,
      key?: string,
      adjustFilter?: ((filter: FilterDescriptor | Array<FilterDescriptor>) => FilterDescriptor | Array<FilterDescriptor>),
      adjustSort?: ((sort?: SortDescriptor<TItem> | Array<SortDescriptor<TItem>>) => SortDescriptor<TItem> | Array<SortDescriptor<TItem>>)
    ): DataSource<TItem, TKey> {
    const self = this;
    const adjustFilterCallback = adjustFilter;

    function isNotEmpty(value: TKey): boolean {
      const tmp = value !== undefined && value !== null;
      if (typeof value === 'number') return tmp && (<number>value) > -1;
      if (typeof value === 'string') return tmp && (<string>value) !== '';
      if (Array.isArray(value)) return tmp && (<Array<string>>value).length > 0;
      return false;
    }


    function toPromise<T>(o: Observable<T>): PromiseLike<T> {
      return new Promise((resolve, reject) => {
        o.subscribe({
          next: res => {
            resolve(res);
          },
          error: (err: HttpErrorResponse) => {
            reject(err)
          }
        })
      }) as Promise<T>;
    }

    return new DataSource<TItem, TKey>({
      store:
        new ZeroxCustomStore<TItem, TKey>({
          loadMode: "processed",
          cacheRawData: false,

          key: 'Id',

          remove: function (key: TKey): Promise<any> {
            try {
              return service._delete(key).toPromise();
            } catch (e) {
              console.error('Service does not have _delete method');
              return Promise.resolve();
            }
          },

          byKey: function (key: TKey): PromiseLike<TItem> {
            let tmpKey = key + '';
            if (!isNotEmpty(key)) {
              return Promise.resolve(<TItem>{});
            }

            try {
              return service.getOne(tmpKey).toPromise();      
            } catch (e) {
              console.error('Service does not have getOne method');
              return Promise.resolve(<TItem>{});
            }
          },

          load: function (options: LoadOptions): PromiseLike<LoadResult<TItem>> {
            return toPromise(this.loadAsObservable(options));
          },

          loadAsObservable: function (options: LoadOptions): Observable<LoadResult<TItem>> {
            // extend filter for combos
            if (options["searchExpr"] != undefined && options["searchOperation"] != undefined && isNotEmpty(options["searchValue"])) {
              if (options["filter"] != null) {
                options["filter"] = [
                  options["filter"],
                  "and",
                  [options["searchExpr"], options["searchOperation"], options["searchValue"]]
                ];
              } else {
                options["filter"] = [options["searchExpr"], options["searchOperation"], options["searchValue"]];
              }
            }

            // extend filter by external function
            if (adjustFilterCallback != null) {
              options["filter"] = adjustFilterCallback(options["filter"] as Array<string> | null);
            }

            //*************
            //OVERRIDE SORT
            //*************
            if (adjustSort != null) {
              options.sort = adjustSort(options.sort);
            }

            if (key != null) {
              try {
                return service.trashListMedium_1(key, options);
              } catch (e) {
                console.error('Service does not have trashListMedium_1 method', e);
                return new Observable<LoadResult<TItem>>();
              }
            }

            if (service instanceof MediumService) {
              try {
                return service.trashListMedium(options as DataSourceLoadOptions);
              } catch (e) {
                console.error('Service does not have trashListMedium method');
                return new Observable<LoadResult<TItem>>();
              }
            } else {
              try {
                return service.trashList(options);
              } catch (e) {
                console.error('Service does not have trashList method');
                return new Observable<LoadResult<TItem>>();
              }
            }
          }
        })
    });
  }
}
